import { useEffect, useState } from 'react';
import { FiTrash2, FiPlus, FiEdit2, FiX } from 'react-icons/fi';
import { getFormFields, createFormField, updateFormField, deleteFormField } from '../../api';
import TagInput from '../../components/admin/TagInput';
import { useToast } from '../../components/Toast';
import Loader from '../../components/Loader';

const TYPES = ['text', 'email', 'tel', 'number', 'textarea', 'select', 'radio', 'checkbox'];
const needsOptions = (t) => ['select', 'radio', 'checkbox'].includes(t);
const blank = { label: '', key: '', type: 'text', placeholder: '', options: [], required: false, fullWidth: false, order: 0, active: true };

export default function AdminFormFields() {
  const [fields, setFields] = useState(null);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast, ToastHost } = useToast();

  const load = () => getFormFields().then(setFields).catch(() => setFields([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(blank); setEditId(null); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast('Label is required', 'error');
    if (needsOptions(form.type) && form.options.length === 0) return toast('Add at least one option', 'error');
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editId) await updateFormField(editId, payload);
      else await createFormField(payload);
      toast('Field saved');
      reset();
      load();
    } catch (err) { toast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const edit = (f) => {
    setEditId(f._id);
    setForm({ label: f.label, key: f.key, type: f.type, placeholder: f.placeholder || '', options: f.options || [], required: !!f.required, fullWidth: !!f.fullWidth, order: f.order || 0, active: f.active !== false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete field "${f.label}"?`)) return;
    await deleteFormField(f._id);
    toast('Field deleted');
    load();
  };

  const toggleActive = async (f) => { await updateFormField(f._id, { active: !f.active }); load(); };

  if (!fields) return <Loader />;

  return (
    <div>
      <div className="acontent__head">
        <div>
          <h1>Query Form Builder</h1>
          <p className="muted" style={{ fontSize: 14 }}>These fields render on the Contact page & product/service enquiry forms.</p>
        </div>
      </div>

      <form className="acard" onSubmit={save}>
        <div className="between" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{editId ? 'Edit field' : <><FiPlus /> Add field</>}</h3>
          {editId && <button type="button" className="btn btn-ghost btn-sm" onClick={reset}><FiX /> Cancel edit</button>}
        </div>
        <div className="form-grid">
          <div className="field"><label>Label <span className="req">*</span></label><input className="input" value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="e.g. Full Name" /></div>
          <div className="field"><label>Key (optional)</label><input className="input" value={form.key} onChange={(e) => set('key', e.target.value)} placeholder="auto from label" /></div>
          <div className="field">
            <label>Type</label>
            <select className="select" value={form.type} onChange={(e) => set('type', e.target.value)}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Placeholder</label><input className="input" value={form.placeholder} onChange={(e) => set('placeholder', e.target.value)} /></div>
          {needsOptions(form.type) && (
            <div className="field full">
              <label>Options (for {form.type})</label>
              <TagInput value={form.options} onChange={(v) => set('options', v)} placeholder="Type an option and press Enter" />
            </div>
          )}
          <div className="field"><label>Order</label><input className="input" type="number" value={form.order} onChange={(e) => set('order', e.target.value)} /></div>
          <div className="field flex center gap"><button type="button" className={`toggle ${form.required ? 'on' : ''}`} onClick={() => set('required', !form.required)} /><label style={{ margin: 0 }}>Required</label></div>
          <div className="field flex center gap"><button type="button" className={`toggle ${form.fullWidth ? 'on' : ''}`} onClick={() => set('fullWidth', !form.fullWidth)} /><label style={{ margin: 0 }}>Full width</label></div>
        </div>
        <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editId ? 'Update field' : 'Add field'}</button>
      </form>

      <div className="table-wrap">
        <table className="atable">
          <thead><tr><th>Order</th><th>Label</th><th>Key</th><th>Type</th><th>Required</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {fields.length === 0 && <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 30 }}>No fields yet — add one above.</td></tr>}
            {fields.map((f) => (
              <tr key={f._id}>
                <td>{f.order}</td>
                <td>{f.label}{f.options?.length ? <div className="muted" style={{ fontSize: 12 }}>{f.options.join(', ')}</div> : null}</td>
                <td><code style={{ fontSize: 12 }}>{f.key}</code></td>
                <td><span className="badge">{f.type}</span></td>
                <td>{f.required ? '✔' : '—'}</td>
                <td><button className={`toggle ${f.active ? 'on' : ''}`} onClick={() => toggleActive(f)} /></td>
                <td><div className="row-actions">
                  <button className="icon-btn" onClick={() => edit(f)}><FiEdit2 /></button>
                  <button className="icon-btn danger" onClick={() => remove(f)}><FiTrash2 /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ToastHost />
    </div>
  );
}
